/**
 * The query model — one shape, two presentations (D7).
 *
 * The page's URL *is* the API query. Filters, sort and column choice are held
 * in exactly the parameter names `GET /lenses` accepts, so the shareable view
 * state and the request are the same string rather than two encodings that can
 * drift apart. Opening a shared link reproduces the view because there is
 * nothing to reconstruct (REQ-WEB-002).
 *
 * The one thing deliberately kept out of the URL is the cursor. A shared link
 * should open the query, not page seven of someone else's scroll.
 */
import { humaniseComputed } from "./formula";
import type { Attribute, Registry } from "./api";

export type Op = "eq" | "lt" | "lte" | "gt" | "gte" | "in" | "contains" | "isnull";

export interface Filter {
  key: string;
  op: Op;
  value: string;
}

/**
 * A user-defined column (REQ-FX-010, REQ-FX-014).
 *
 * The formula is carried as text, not as a tree. The server parses it on every
 * request through the full allowlist, so there is no client-side "validated"
 * state that could disagree with what is actually evaluated (REQ-FX-012).
 */
export interface Computed {
  name: string;
  formula: string;
}

export interface Query {
  columns: string[];
  filters: Filter[];
  sortKey: string;
  sortDesc: boolean;
  computed: Computed[];
  /** REQ-WEB-037: the search text as typed; the API splits it into words. */
  q: string;
  /**
   * REQ-WEB-022: how many specs a link asked for beyond MAX_COLUMNS. Set only
   * by parseQuery, so the page can say what it left out instead of silently
   * dropping it; anything else leaves it undefined.
   */
  columnsDropped?: number;
  /**
   * REQ-FX-011 ("degrade gracefully"): parts of a link this page could not
   * honour and left out, in plain words. Set only by parseQuery so the page can
   * say so instead of quietly rewriting the address.
   */
  ignored?: string[];
}

/**
 * Specs shown under each result on a phone (REQ-WEB-022: "bounded to a legible
 * number"). The picker stops at this; a link asking for more is cut to it here.
 */
export const MAX_COLUMNS = 8;

/**
 * Newest first by release date (REQ-WEB-004 as amended by D50, REQ-WEB-042):
 * the order a shopper expects. Lenses with no release date follow, last, in
 * both directions (the server's NULLS LAST). It replaced D48's stopgap, name
 * A–Z, which itself replaced "lightest first".
 */
export const DEFAULT_SORT = "released";
export const DEFAULT_SORT_DESC = true;

/**
 * Columns shown when the user has not chosen any.
 *
 * Count these against what actually appears as a *spec* under a result, which
 * is fewer than the list length: the sorted attribute is promoted to the
 * headline, and the two focal-length columns collapse into the one range that
 * gets printed beside the name. The default sort is by release date, which is
 * promoted to the headline, so the six below render as four specs, the focal
 * range and the release date, inside MAX_COLUMNS.
 */
export const DEFAULT_COLUMNS = [
  "weight_g",
  "focal_length_min_mm",
  "focal_length_max_mm",
  "aperture_max_wide",
  "min_focus_distance_mm",
  "length_mm",
];

const FILTER_RE = /^filter\[([A-Za-z0-9_]+)\]\[([a-z]+)\]$/;
const COMPUTED_RE = /^computed\[([A-Za-z0-9_]+)\]$/;

export function parseQuery(search: string, registry: Registry | null): Query {
  const p = new URLSearchParams(search);

  // Computed columns are read first: a name defined in this same URL is a
  // known key for everything below it, so a shared link that sorts by its own
  // computed column survives the stale-key filter rather than falling back to
  // the default sort.
  const computed: Computed[] = [];
  const ignored: string[] = [];
  for (const [name, formula] of p.entries()) {
    const m = COMPUTED_RE.exec(name);
    if (m && m[1] && formula.trim() !== "") {
      computed.push({ name: m[1], formula });
    } else if (name.startsWith("computed[")) {
      // Not echoed: the name is exactly what failed to be a name.
      ignored.push("a calculated column with a name that isn't allowed");
    }
  }
  const computedNames = new Set(computed.map((c) => c.name));

  const known = registry ? new Set(registry.attributes.map((a) => a.key)) : null;
  const keep = (k: string) =>
    !known || known.has(k) || IDENTITY_KEYS.has(k) || computedNames.has(k);

  const rawSort = (p.get("sort") ?? (DEFAULT_SORT_DESC ? "-" : "") + DEFAULT_SORT).trim();
  const sortDesc = rawSort.startsWith("-");
  let sortKey = sortDesc ? rawSort.slice(1) : rawSort;
  // A link shared before #215 asked to sort "by name" with the only key that
  // existed then, model_name — which sorts by the model alone and now looks
  // shuffled beside the displayed maker + model name, and matches no option in
  // the sort menu. `name` is what such a link meant (REQ-WEB-004 amended, C8,
  // 2026-09-29 QA pass, #287).
  if (sortKey === "model_name") sortKey = "name";

  if (!keep(sortKey)) ignored.push(`sorting by “${sortKey}” (sorted by the default order instead)`);

  const wanted = (p.get("columns") ?? "")
    .split(",")
    .map((s) => s.trim())
    .filter((s) => {
      if (s === "") return false;
      if (keep(s)) return true;
      ignored.push(`the column “${s}”`);
      return false;
    });
  // A shared or hand-edited link can ask for more than the picker allows. Keep
  // the first MAX_COLUMNS, in the link's order, and report the rest.
  const columns = wanted.slice(0, MAX_COLUMNS);

  const filters: Filter[] = [];
  for (const [name, value] of p.entries()) {
    const m = FILTER_RE.exec(name);
    if (!m) continue;
    const [, key, op] = m;
    if (!key || !op) continue;
    if (!keep(key)) {
      ignored.push(`a filter on “${key}”`);
      continue;
    }
    filters.push({ key, op: op as Op, value });
  }

  return {
    columns: columns.length ? columns : DEFAULT_COLUMNS.filter(keep),
    ...(wanted.length > columns.length ? { columnsDropped: wanted.length - columns.length } : {}),
    ...(ignored.length ? { ignored: [...new Set(ignored)] } : {}),
    filters,
    sortKey: keep(sortKey) ? sortKey : DEFAULT_SORT,
    sortDesc: keep(sortKey) ? sortDesc : DEFAULT_SORT_DESC,
    computed,
    q: p.get("q") ?? "",
  };
}

/** Identity fields live on the lens row rather than in the attribute registry,
 *  so they are filterable and sortable without appearing in /attributes. */
export const IDENTITY_KEYS = new Set([
  "product_code",
  "model_name",
  "name",
  "manufacturer",
  "mount",
  "coverage",
  "lens_type",
  "announced_on",
  "discontinued_on",
]);

export function toSearch(q: Query, extra?: Record<string, string>): string {
  const p = new URLSearchParams();
  // First, because it is what a person typed and reads for in a shared link.
  if (q.q.trim()) p.set("q", q.q.trim());
  if (q.columns.length) p.set("columns", q.columns.join(","));
  p.set("sort", (q.sortDesc ? "-" : "") + q.sortKey);
  // Before the filters, so a link reads definition-then-use. The API does not
  // care about order; a person reading the URL does.
  for (const c of q.computed) p.set(`computed[${c.name}]`, c.formula);
  for (const f of q.filters) p.set(`filter[${f.key}][${f.op}]`, f.value);
  for (const [k, v] of Object.entries(extra ?? {})) p.set(k, v);
  return p.toString();
}

/**
 * The API only returns cells for columns that were asked for, and it sorts by a
 * key that need not be one of them. D7 makes the sorted attribute the largest
 * value in every row, so it must always be fetched — otherwise the number the
 * list is ordered by would be the one number missing from it.
 */
export function requestColumns(q: Query, needCoverage = false): string[] {
  const cols = [...q.columns];
  if (!IDENTITY_KEYS.has(q.sortKey) && !cols.includes(q.sortKey)) cols.unshift(q.sortKey);
  // REQ-WEB-026: lenses shown under the smaller-coverage opt-in must be
  // visibly marked. A row cannot be marked without knowing what it covers, and
  // `coverage` lives on the lens row, so asking for it costs no extra join.
  if (needCoverage && !cols.includes("coverage")) cols.push("coverage");
  return cols;
}

export function isComputed(q: Query, key: string): boolean {
  return q.computed.some((c) => c.name === key);
}

/**
 * Remove a computed column and everything that referred to it.
 *
 * Dropping the definition alone would leave `sort=density` pointing at a name
 * the API no longer knows, and the whole request would 422 — one deletion
 * breaking the page rather than removing a column.
 */
export function withoutComputed(q: Query, name: string): Query {
  const computed = q.computed.filter((c) => c.name !== name);
  const columns = q.columns.filter((k) => k !== name);
  const filters = q.filters.filter((f) => f.key !== name);
  const dropped = q.sortKey === name;
  const sortKey = dropped ? DEFAULT_SORT : q.sortKey;
  const sortDesc = dropped ? DEFAULT_SORT_DESC : q.sortDesc;
  return { ...q, computed, columns, filters, sortKey, sortDesc };
}

export function filterId(f: Filter): string {
  return `${f.key}:${f.op}`;
}

export function withFilter(q: Query, f: Filter): Query {
  const rest = q.filters.filter((x) => filterId(x) !== filterId(f));
  return { ...q, filters: [...rest, f] };
}

export function withoutFilter(q: Query, key: string, op?: Op): Query {
  return {
    ...q,
    filters: q.filters.filter((x) => !(x.key === key && (op === undefined || x.op === op))),
  };
}

/** Every filter currently set on one attribute, keyed by operator. */
export function filtersFor(q: Query, key: string): Partial<Record<Op, string>> {
  const out: Partial<Record<Op, string>> = {};
  for (const f of q.filters) if (f.key === key) out[f.op] = f.value;
  return out;
}

export function attributeByKey(registry: Registry | null, key: string): Attribute | undefined {
  return registry?.attributes.find((a) => a.key === key);
}

/** How an attribute is named in prose, including its display unit. */
export function labelWithUnit(attr: Attribute | undefined, key: string): string {
  // Same fallback as `shortLabel`, for the same reason and the same one case.
  // It was fixed there alone (#151) and left here, so the raw key kept
  // appearing — in the spec row this time, which is the surface a phone shows
  // most of. A computed column has no registry attribute and no unit, so there
  // is nothing to append.
  //
  // This is the label for a DOWNLOAD (csv.ts): every unit stays, because a
  // file has no "f/2.8" beside its numbers. On screen use `labelParts`.
  if (!attr) return humaniseComputed(key);
  const u = attr.display_unit;
  return u && u !== "x" && attr.unit_family !== "none" ? `${attr.label} (${u})` : attr.label;
}

/**
 * A spec's name and, separately, the unit worth printing after it, for the
 * screen (the sort menu, a filter's heading, the table header).
 *
 * No unit is printed for a count ("n": elements, groups, blades) or an aperture
 * ("f": its values already read "f/2.8"), nor for a ratio ("x") (#213, owner
 * 2026-10-03): "Elements (n)" and "Max aperture (wide) (f)" read as typos. A
 * computed column has no registry attribute and no unit (see `labelWithUnit`).
 */
export function labelParts(
  attr: Attribute | undefined, key: string,
): { name: string; unit: string | null } {
  if (!attr) return { name: humaniseComputed(key), unit: null };
  const u = attr.display_unit;
  const quiet = u === "x" || u === "n" || u === "f";
  return {
    name: attr.label,
    unit: u && !quiet && attr.unit_family !== "none" ? u : null,
  };
}

/** `labelParts` as one string: "Weight (g)", "Elements". */
export function shownLabel(attr: Attribute | undefined, key: string): string {
  const { name, unit } = labelParts(attr, key);
  return unit ? `${name} (${unit})` : name;
}

/** The short form where space is tight; the full label when there is none (#67). */
export function shortLabel(attr: Attribute | undefined, key: string): string {
  // The fallback fires for exactly one thing: a computed column, which has no
  // registry attribute by definition. Returning the bare key put
  // `LENGTH_MM_TIMES_DIAMETER_MM` on every result card (#151), so the fallback
  // reads it the way a person would say it.
  return attr?.short_label ?? attr?.label ?? humaniseComputed(key);
}

/**
 * Every list of attributes the user reads is ordered the same way: by the
 * registry's own groups, alphabetically within each.
 *
 * Registry `sort_order` is per-group, so using it across groups produced an
 * order that looked arbitrary — "Minimum focus distance, Focal length (wide),
 * Weight, Maximum magnification" — and the owner said so. Alphabetical is
 * predictable; the grouping is the registry's own structure rather than
 * something invented here.
 */
export function byGroupThenName(attrs: Attribute[]): [string, Attribute[]][] {
  const m = new Map<string, Attribute[]>();
  for (const a of attrs) {
    const list = m.get(a.group) ?? [];
    list.push(a);
    m.set(a.group, list);
  }
  for (const list of m.values()) {
    list.sort((x, y) => shortLabel(x, x.key).localeCompare(shortLabel(y, y.key)));
  }
  return [...m.entries()].sort((x, y) => x[0].localeCompare(y[0]));
}

/**
 * The lens's own identity — who made it, whether it zooms, what it covers.
 *
 * These are not registry attributes: they live on the lens row, so they never
 * appeared in a picker built from `/attributes` and were invisible as filters
 * even though the API has always accepted them (#72). Brand and mount are
 * independent axes (REQ-DM-020), so brand belongs here with the preferences
 * rather than in the camera selector, which is a compatibility gate.
 *
 * Mount is deliberately absent: it is the camera selector's job (REQ-WEB-025)
 * and offering it twice would let a visitor set two answers to one question.
 */
export interface IdentityFilter {
  key: string;
  label: string;
  /** `in` for many-of, `eq` for one-of. */
  op: Op;
  group: string;
  /** Options are supplied by the caller from live data, never hardcoded. */
  optionsFrom: "brands" | "coverages" | "static";
  options?: { value: string; label: string }[];
}

export const IDENTITY_FILTERS: IdentityFilter[] = [
  {
    key: "manufacturer", label: "Brand", op: "in", group: "identity",
    optionsFrom: "brands",
  },
  {
    key: "lens_type", label: "Prime or zoom", op: "eq", group: "identity",
    optionsFrom: "static",
    options: [{ value: "prime", label: "Prime" }, { value: "zoom", label: "Zoom" }],
  },
  {
    // Options come from `/mounts`, which serves the coverage vocabulary with
    // its ranks. They were hardcoded to full frame and APS-C, which silently
    // hid **279 Four Thirds lenses** — they were in the data and in the
    // vocabulary the whole time, and no filter would show them.
    //
    // That is the bug `app.tsx` warns about in its own opening comment: "If you
    // find yourself about to type a lens spec's name into this file, that is
    // the bug." A hardcoded list cannot be wrong on the day it is written and
    // cannot stay right afterwards.
    key: "coverage", label: "Sensor size covered", op: "eq", group: "identity",
    optionsFrom: "coverages",
  },
];

export const IDENTITY_FILTER_KEYS = new Set(IDENTITY_FILTERS.map((f) => f.key));
